import React, { createContext, useContext, useState, useCallback } from 'react';

interface ImageHistory {
  id: string;
  name: string;
  timestamp: number;
}

interface HistoryContextType {
  imageHistory: ImageHistory[];
  addToHistory: (id: string, name: string) => void;
  removeFromHistory: (id: string) => void;
  clearHistory: () => void;
  getRecentHistory: (limit?: number) => ImageHistory[];
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

export const useHistoryContext = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useHistoryContext must be used within a HistoryProvider');
  }
  return context;
};

interface HistoryProviderProps {
  children: React.ReactNode;
}

export const HistoryProvider: React.FC<HistoryProviderProps> = ({ children }) => {
  const [imageHistory, setImageHistory] = useState<ImageHistory[]>([]);

  const addToHistory = useCallback((id: string, name: string) => {
    setImageHistory(prev => {
      // Remove if already exists to avoid duplicates
      const filtered = prev.filter(item => item.id !== id);
      // Add to beginning of array
      return [{ id, name, timestamp: Date.now() }, ...filtered];
    });
  }, []);

  const removeFromHistory = useCallback((id: string) => {
    setImageHistory(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setImageHistory([]);
  }, []);

  const getRecentHistory = useCallback((limit: number = 10) => {
    return imageHistory.slice(0, limit);
  }, [imageHistory]);

  const value: HistoryContextType = {
    imageHistory,
    addToHistory,
    removeFromHistory,
    clearHistory,
    getRecentHistory,
  };

  return (
    <HistoryContext.Provider value={value}>
      {children}
    </HistoryContext.Provider>
  );
}; 